import { memo, useEffect, useRef, useState } from "react";
import currencyFormat from "@utility/numbers/currencyFormat";

function GameJackpot({
  jackpot,
  time_update,

  // ใช้คำนวณ "ค่าจริงตามเวลา" จาก time_update
  baseRate = 3,

  // Phase 2: ช่วงการสุ่มเรทตอนขยับ (เหมือนโค้ดเดิม)
  minRandomRate = 50,  // สุ่มขั้นต่ำ (เช่น ขยับทีละ 50 บาท/วินาที)
  maxRandomRate = 5000, // สุ่มสูงสุด (เช่น ขยับทีละ 5000 บาท/วินาที)

  // Phase 2: ช่วงเวลาการรอแต่ละรอบ
  minSeconds = 1,      // รออย่างน้อย 1 วิ
  maxMinutes = 1,      // รออย่างมาก 1 นาที
}) {
  // ---------- CONFIG Phase 1: ไล่ตามให้ทันภายใน 3 วิ ----------
  const CATCHUP_DURATION = 3000;      // รวมเวลา phase 1 = 3,000 ms (3 วิ)
  const CATCHUP_INTERVAL = 50;        // อัปเดตทุก 50 ms
  const CATCHUP_STEPS = CATCHUP_DURATION / CATCHUP_INTERVAL; // 3000 / 50 = 60 ครั้ง

  // ฟังก์ชันคำนวณ "ค่าจริง ณ ตอนนี้" จาก time_update + baseRate
  const getRealValueFromTimeUpdate = () => {
    const base = jackpot || 0;

    if (!time_update) return base;

    const ts = new Date(time_update).getTime();
    if (Number.isNaN(ts)) return base;

    const now = Date.now();
    const diffSec = Math.max(0, (now - ts) / 1000);

    return base + diffSec * baseRate;
  };

  // เริ่มต้น: แสดง jackpot เดิมก่อนตามที่ขอ (ยังไม่บวก time_update)
  const [displayValue, setDisplayValue] = useState(() => jackpot || 0);

  // เก็บค่าเป้าหมายที่ต้องการไล่ไปให้ถึง (jackpot + ส่วนต่างของ time_update)
  const targetValueRef = useRef(getRealValueFromTimeUpdate());

  // ใช้นับจำนวน step ที่เดินไปแล้วใน Phase 1
  const catchupStepRef = useRef(0);

  // เก็บเวลาอ้างอิงสำหรับ Phase 2 (ใช้หา deltaTimeSec)
  const lastUpdateRef = useRef(Date.now());

  // ตัวจับ timeout ปัจจุบัน
  const timeoutRef = useRef(null);

  // Phase ปัจจุบัน:
  // false = Phase 1 (ไล่ตาม time_update ภายใน 3 วิ)
  // true  = Phase 2 (ใช้สุ่มเหมือนเดิม)
  const [isCatchupDone, setIsCatchupDone] = useState(false);

  // ถ้า jackpot / time_update / baseRate เปลี่ยน → รีเซ็ตค่าใหม่
  useEffect(() => {
    const base = jackpot || 0;
    const target = getRealValueFromTimeUpdate();

    targetValueRef.current = target;
    catchupStepRef.current = 0;
    lastUpdateRef.current = Date.now();

    // แสดง "ค่าเดิม" ก่อนเสมอ
    setDisplayValue(base);

    // ถ้าต่างกันนิดเดียว หรือ target <= base → ไม่ต้องไล่ตามแล้ว ข้ามไป Phase 2
    if (!time_update || !Number.isFinite(target) || target <= base + 0.0001) {
      setIsCatchupDone(true);
    } else {
      setIsCatchupDone(false); // มีส่วนต่าง → เข้า Phase 1 ไล่ตาม 3 วิ
    }
  }, [jackpot, time_update, baseRate]);

  useEffect(() => {
    const runUpdateLoop = () => {
      // ---------------- PHASE 1: ไล่ตาม time_update ให้ทันภายใน 3 วิ ----------------
      if (!isCatchupDone) {
        const target = targetValueRef.current;

        setDisplayValue((prev) => {
          // เซฟกัน NaN / Infinity
          if (!Number.isFinite(target)) {
            setIsCatchupDone(true);
            return prev;
          }

          // ถ้าแตะเป้าแล้ว หรือเกิน → จบ Phase 1
          if (prev >= target) {
            setIsCatchupDone(true);
            return target;
          }

          catchupStepRef.current += 1;

          // คำนวณจำนวน step ที่เหลืออยู่
          const stepsLeft = Math.max(
            1,
            CATCHUP_STEPS - catchupStepRef.current
          );

          // เพิ่มทีละ (ส่วนต่าง / step ที่เหลือ) เพื่อให้จบใน <= 3 วิ
          const diff = target - prev;
          const stepIncrease = diff / stepsLeft;

          const next = prev + stepIncrease;

          // กัน overshoot
          if (next >= target) {
            setIsCatchupDone(true);
            return target;
          }

          return next;
        });

        // Phase 1: อัปเดตถี่ ๆ ทุก 50 ms
        timeoutRef.current = setTimeout(runUpdateLoop, CATCHUP_INTERVAL);
        return;
      }

      // ---------------- PHASE 2: โหมดสุ่ม (เหมือนโค้ดเดิม) ----------------
      const now = Date.now();
      const deltaTimeSec = (now - lastUpdateRef.current) / 1000;
      lastUpdateRef.current = now;

      // ช่วงเวลา delay ที่จะรอรอบถัดไป
      const minMs = minSeconds * 1000;
      const maxMs = maxMinutes * 60 * 1000;
      const randomDelay = minMs + Math.random() * (maxMs - minMs);

      // สุ่มเรทเพิ่ม
      const currentRandomRate =
        minRandomRate + Math.random() * (maxRandomRate - minRandomRate);

      const addedValue = deltaTimeSec * currentRandomRate;

      setDisplayValue((prev) => prev + addedValue);

      timeoutRef.current = setTimeout(runUpdateLoop, randomDelay);
    };

    // เริ่มทำงานรอบแรกหลัง 1 วิ (ให้เห็น jackpot เดิมนิ่ง ๆ ก่อนแพล็บหนึ่ง)
    timeoutRef.current = setTimeout(runUpdateLoop, 1000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isCatchupDone, minSeconds, maxMinutes, minRandomRate, maxRandomRate]);

  return (
    <div className="text-3xl font-bold font-mono bg-black p-4 rounded-lg text-center">
      {/* key เปลี่ยน = เล่น animation ใหม่ทุกครั้งที่เลขเปลี่ยน */}
      <span key={displayValue} className="jackpot-countup">
        ฿ {currencyFormat(displayValue, 0)}
      </span>
    </div>
  );
}

export default memo(GameJackpot);
